import {
  getAllowedNoFollow,
  getDescription,
  getTitleTag
} from '../../helpers/browse-helpers';
import {
  getBrowseSearchStructuredData,
  getBreadcrumbStructuredData,
  getEmtStructuredData
} from '../../structured-data';
import { HD_URL, BRANDS_PAGE_URL } from '../../constants';

/**
 * Generates a canonical URL for a price range refinement.
 *
 * @param {Object} priceRangeRefinement - An object containing valid price range keys.
 * @param {Object} location - The location object with `pathname` and `search` properties.
 * @param {string} defaultCanonical - The default canonical URL.
 * @returns {string|null} The canonical URL for the price range, or `null` if not applicable.
 */
export const getCanonicalUrlForPriceRange = (priceRangeRefinement, location, defaultCanonical) => {
  if (!priceRangeRefinement) return null;

  const { pathname = '', search = '' } = location;
  const searchParams = new URLSearchParams(search);

  const lowerBound = searchParams.get('lowerBound') || searchParams.get('lowerbound') || 0;
  const upperBound = searchParams.get('upperBound') || searchParams.get('upperbound') || 0;
  const priceKey = `lb-${lowerBound}-ub-${upperBound}`;

  if (!priceRangeRefinement[priceKey]) return null;

  // since we have a match in the file (line above not null), use the searchModel value for canonical
  const {
    pathname: canonicalPathname,
    origin,
    searchParams: canonicalSearchParams
  } = new URL(defaultCanonical);

  if (canonicalSearchParams?.size <= 2) return defaultCanonical;

  // If there's more than 2 params, that means there's more than lower and upper bound.
  // In that case, reset canonical using searchModel canonical and setting only lower and upper bound params.
  return `${origin}${canonicalPathname}?lowerBound=${lowerBound}&upperBound=${upperBound}`;
};

/**
 * Determines the value for the robots meta tag based on canonical exceptions.
 *
 * @param {string} pathWithQueryParams - The current path with query parameters.
 * @param {string} canonicalUrl - The canonical URL for the page.
 * @param {string|null} priceRangeExceptionCanonical - Exception flag for price range canonical.
 * @param {boolean} searchPageIndexingExceptionCanonical - Exception flag for search page indexing canonical.
 * @returns {boolean} Returns `false` if an exception is present, otherwise returns the result of `getAllowedNoFollow`.
 */
export const getRobotsTag = (
  pathWithQueryParams,
  canonicalUrl,
  priceRangeExceptionCanonical,
  searchPageIndexingExceptionCanonical
) => {
  return priceRangeExceptionCanonical || searchPageIndexingExceptionCanonical
    ? false
    : getAllowedNoFollow(pathWithQueryParams, canonicalUrl);
};

/**
 * Generates schema.org structured data tags for a browse/search or content page.
 *
 * @param {boolean} isContentPageOnly - Indicates if the page is content-only.
 * @param {Array} processedBreadcrumbs - Breadcrumbs data for structured data.
 * @param {string} title - The page title.
 * @param {string} canonical - The canonical URL for the page.
 * @param {Array} faqs - FAQ data for the page.
 * @param {Object|null} emtSchema - EMT schema data, if available.
 * @param {Array} products - List of products for the page.
 * @returns {Array<Object>} Array of schema tag objects with `content` and `id` properties.
 */
export const getSchemaTag = (
  isContentPageOnly,
  processedBreadcrumbs,
  title,
  canonical,
  faqs,
  emtSchema,
  products
) => {
  if (isContentPageOnly) {
    const breadcrumbStructuredData = getBreadcrumbStructuredData({
      canonical,
      breadcrumbs: processedBreadcrumbs,
      title
    });

    return [{ content: breadcrumbStructuredData, id: 'breadcrumbStructuredData' }];
  }
  const schema = emtSchema
    ? getEmtStructuredData({
      faqs,
      schema: emtSchema,
      breadcrumbs: processedBreadcrumbs
    })
    : getBrowseSearchStructuredData({
      breadcrumbs: processedBreadcrumbs,
      canonical,
      faqs,
      skus: products,
      title
    });

  return [{ content: schema, id: 'browseSearchStructuredData' }];
};

/**
 * Builds the breadcrumb list for a stand-alone Brand Page's structured data, replacing the
 * single raw brand crumb with Home -> Brands -> <Brand Name>. The Android alternate-link tag
 * is unaffected and keeps using the real brand crumb.
 *
 * @param {Array<Object>} processedBreadcrumbs - The page's resolved breadcrumbs. For a
 *   Stand-Alone Brand Page this is the raw brand crumb (not the parsed/filtered trail),
 *   since parseCrumbs drops it due to its deselectUrl.
 * @param {string} canonical - The resolved canonical URL for the page.
 * @returns {Array<Object>} Breadcrumbs to use for schema.org structured data.
 */
export const getStandAloneBrandPageCrumbs = (processedBreadcrumbs, canonical) => ([
  { label: 'Brands', id: `${HD_URL}${BRANDS_PAGE_URL}` },
  { label: processedBreadcrumbs?.[0]?.label, id: canonical }
]);

/**
 * Determines the type label for a browse/search page based on view and page flags.
 *
 * @param {boolean} isContentPageOnly - Indicates if the page is content-only.
 * @param {boolean} isBrandPage - Indicates if the page is a brand page.
 * @param {boolean} isHybrid - Indicates if the page is a hybrid page.
 * @returns {string} The type label for the page.
 */
export const getType = (isContentPageOnly, isBrandPage, isHybrid) => {
  if (isContentPageOnly) {
    return isBrandPage ? 'Category brand' : 'Category';
  }
  if (isHybrid) return `cat PLP${isBrandPage ? ' brand' : ''}`;

  if (isBrandPage) return 'PLP brand';

  return 'PLP';
};

/**
 * Generates the title for a browse/search page.
 *
 * @param {Object} params - Parameters for generating the title.
 * @param {boolean} params.isContentPageOnly - Indicates if the page is content-only.
 * @param {string} params.parsedTemplatesTitle - The parsed template title.
 * @param {string} params.pageType - The type of the page.
 * @param {Array} params.categories - The categories for the page.
 * @param {Array} params.refinements - The refinements for the page.
 * @param {string} params.searchReportKeyword - The search keyword.
 * @param {string} params.pathWithQueryParams - The path with query parameters.
 * @returns {string} The generated title for the page.
 */
export const getBrowseSearchTitle = ({
  isContentPageOnly,
  parsedTemplatesTitle,
  pageType,
  categories,
  refinements,
  searchReportKeyword,
  pathWithQueryParams
}) => {
  if (isContentPageOnly) {
    return parsedTemplatesTitle;
  }

  return getTitleTag({
    pageType, categories, refinements, searchVal: searchReportKeyword, pathWithQueryParams
  });
};

/**
 * Generates the description for a browse/search page.
 *
 * @param {Object} params - Parameters for generating the description.
 * @param {string} params.pageType - The type of the page.
 * @param {Array} params.categories - The categories for the page.
 * @param {Array} params.refinements - The refinements for the page.
 * @param {string} params.searchReportKeyword - The search keyword.
 * @param {boolean} params.isContentPageOnly - Indicates if the page is content-only.
 * @param {string} params.parsedTemplatesDescription - The parsed template description.
 * @returns {string} The generated description for the page.
 */
export const getBrowseSearchDescription = ({
  pageType, categories, refinements, searchReportKeyword, isContentPageOnly, parsedTemplatesDescription
}) => {
  if (isContentPageOnly && !(categories[0] === 'Appliances' && pageType === 'b')) {
    return parsedTemplatesDescription;
  }
  return getDescription(pageType, categories, refinements, searchReportKeyword);
};

/**
 * Extracts a search keyword from a given URL path.
 *
 * @param {string} path - The URL path to extract the keyword from.
 * @returns {string} The extracted keyword, or an empty string if none is found or if the keyword is a URL.
 */
export const getKeyword = (path) => {
  let keyword = '';
  const parts = path.replace(/^\/render[^/]*/, '').split('?');
  const leftOfQuery = parts[0] || '';
  let [unusedSegment, context, category, ...pieces] = leftOfQuery.split('/');

  if (context === 's') {
    keyword = category;
  }
  pieces.forEach((piece) => {
    if (/^Ntt-/.test(piece)) {
      keyword = piece.substring(4, piece.length)?.split(/\+|%2B/).join(' ');
    }
  });
  if (keyword) {
    if (keyword.match(/^https?:/)) {
      keyword = '';
    }
  }
  return keyword;
};
